import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil, filter } from 'rxjs';
import { ChartType } from 'chart.js';
import { TeamManagementService } from '../team-management.service';

/** Ordered rating buckets rendered as bars in the chart. Keys match the DB
 *  values on contract_feedback.performance_rating (plus the synthetic
 *  UNRATED bucket returned by the BE for people with no feedback row). Color
 *  is applied per-bar so the chart is scannable at a glance — high performers
 *  green, low performers red, NA/unrated neutral/orange. */
interface RatingBar {
  key: string;
  label: string;
  color: string;
}

// Muted dashboard palette with 8 visually distinct hues. Green→red
// semantic for the numeric scale, distinct grays for the two NA states,
// and purple for Unrated so it clearly reads as "off-scale, no signal"
// rather than blending into the warm end of the ramp.
// `label` is the short text under the bar. `title` is the descriptive
// name used in the hover tooltip ("5 - Exceptional") so managers can see
// what each bar means without opening the details screen.
interface RatingBarWithTitle extends RatingBar {
  title: string;
}

const RATING_BARS: readonly RatingBarWithTitle[] = [
  { key: '5',        label: '5',       title: 'Exceptional',                  color: '#2f6b53' },
  { key: '4',        label: '4',       title: 'Exceeds Expectations',         color: '#8fbca5' },
  { key: '3',        label: '3',       title: 'Meets Expectations',           color: '#d4b25e' },
  { key: '2',        label: '2',       title: 'Partially Meets Expectations', color: '#d47a4a' },
  { key: '1',        label: '1',       title: 'Does Not Meet Expectations',   color: '#a13b3b' },
  { key: 'TOO_NEW',  label: 'Too New', title: 'Too New to Rate',              color: '#c9cdd2' },
  { key: 'N_A',      label: 'N/A',     title: 'Not Applicable',               color: '#6d757d' },
  { key: 'UNRATED',  label: 'Unrated', title: 'Not Rated',                    color: '#7a5db8' },
];

interface QuarterChip {
  value: string;      // Q1..Q4
  label: string;      // "Q1", "Mid Year", "Q3", "Year End"
}

const QUARTER_CHIPS: readonly QuarterChip[] = [
  { value: 'Q1', label: 'Q1' },
  { value: 'Q2', label: 'Mid Year' },
  { value: 'Q3', label: 'Q3' },
  { value: 'Q4', label: 'Year End' },
];

@Component({
  selector: 'app-team-performance-summary',
  templateUrl: './team-performance-summary.component.html',
  styleUrls: ['./team-performance-summary.component.scss'],
})
export class TeamPerformanceSummaryComponent implements OnInit, OnDestroy {
  readonly quarterChips = QUARTER_CHIPS;

  activeQuarter = 'Q2';
  activeYear = new Date().getFullYear();
  loading = false;
  loadError = false;

  /** Year options for the picker — current year plus the two preceding
   *  ones. Shaped as {label, value} for the shared emerald-dropdown so the
   *  control matches the Month picker on the sibling Timesheet Metrics tile. */
  yearOptions: Array<{ label: string; value: number }> = [];

  counts: Record<string, number> = {};
  totalCwr = 0;
  ratedCount = 0;
  unratedCount = 0;

  // ── Chart config for the vertical bar chart. Uses the same
  //    [datasets] + [labels] binding pattern as the RTO tile in
  //    team-management-row-two.component.html so the two tiles behave
  //    consistently under ng2-charts.
  chartType: ChartType = 'bar';
  chartLabels: string[] = RATING_BARS.map((b) => b.label);
  chartDatasets: any[] = [];
  chartOptions: any = this.buildChartOptions();

  private currentEmployeeId: string | null = null;
  private currentFilterType: string | undefined = undefined;
  private readonly destroy$ = new Subject<void>();

  /** Navigate to the full-page details view. Drill-down surface now lives
   *  at /team-management/performance-details — same tree-scoping,
   *  cascading L3/L4/L5 + rating filters, and quarter/year chips. */
  openDetails(): void {
    this.router.navigate(['/team-management/performance-details']);
  }

  constructor(
    private readonly teamService: TeamManagementService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    // Default to the PREVIOUS completed quarter — that's the one managers
    // just finished collecting feedback for, so it has the most useful
    // distribution to look at. The current calendar quarter is typically
    // still in progress with lots of unrated rows.
    // Q1 wraps to Q4 of the prior year.
    const now = new Date();
    const currentQ = Math.floor(now.getMonth() / 3) + 1;
    const prevQ = currentQ === 1 ? 4 : currentQ - 1;
    const prevYear = currentQ === 1 ? now.getFullYear() - 1 : now.getFullYear();
    this.activeQuarter = 'Q' + prevQ;
    this.activeYear = prevYear;
    this.yearOptions = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2]
      .map((y) => ({ label: String(y), value: y }));

    this.teamService
      .getSelectedDropdownValue()
      .pipe(
        takeUntil(this.destroy$),
        filter((selection) => selection !== null),
      )
      .subscribe((selection) => {
        this.currentEmployeeId = selection!.employeeId;
        this.currentFilterType = selection!.filterType;
        this.fetch();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onQuarterChipClick(quarter: string): void {
    if (this.activeQuarter === quarter) return;
    this.activeQuarter = quarter;
    this.fetch();
  }

  /** emerald-dropdown emits either the raw value or a {label,value} pair
   *  depending on the version — normalise both shapes before comparing. */
  onYearChange(event: number | string | { value: number }): void {
    let raw: number | string;
    if (event && typeof event === 'object' && 'value' in event) raw = event.value;
    else raw = event as number | string;
    const y = typeof raw === 'string' ? Number.parseInt(raw, 10) : raw;
    if (!y || y === this.activeYear) return;
    this.activeYear = y;
    this.fetch();
  }


  /** Rated / Total as a whole-number percent for the tile footer. */
  ratedPercent(): number {
    if (this.totalCwr <= 0) return 0;
    return Math.round((this.ratedCount / this.totalCwr) * 100);
  }

  countFor(key: string): number {
    return this.counts[key] ?? 0;
  }

  /** Rebuild chart data from the current counts map. Called after every fetch
   *  so bar heights reflect the freshly-loaded quarter. */
  private rebuildChart(): void {
    const data = RATING_BARS.map((b) => this.counts[b.key] ?? 0);
    const backgroundColor = RATING_BARS.map((b) => b.color);
    this.chartDatasets = [
      {
        data,
        backgroundColor,
        borderRadius: 4,
        maxBarThickness: 32,
      },
    ];
  }

  private buildChartOptions(): any {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            // Tooltip header: "5 - Exceptional" (bar-label + descriptive title)
            title: (items: { label: string; dataIndex: number }[]) => {
              const item = items[0];
              if (!item) return '';
              const bar = RATING_BARS[item.dataIndex];
              return bar ? `${bar.label} - ${bar.title}` : item.label;
            },
            // Body: "12 employees"
            label: (ctx: { parsed: { y: number } }) =>
              `${ctx.parsed.y} employee${ctx.parsed.y === 1 ? '' : 's'}`,
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 11, weight: 600 as unknown as number }, color: '#4b5563' },
        },
        y: {
          beginAtZero: true,
          ticks: { precision: 0, font: { size: 11 }, color: '#6b7280' },
          grid: { color: '#f3f4f6' },
        },
      },
    };
  }

  private fetch(): void {
    if (!this.currentEmployeeId) return;
    this.loading = true;
    this.loadError = false;
    this.teamService
      .getPerformanceRatingSummary(
        this.currentEmployeeId,
        this.activeYear,
        this.activeQuarter,
        this.currentFilterType,
      )
      .subscribe({
        next: (resp) => {
          this.loading = false;
          if (resp?.data) {
            this.counts = resp.data.counts || {};
            this.totalCwr = resp.data.totalCwr || 0;
            this.ratedCount = resp.data.ratedCount || 0;
            this.unratedCount = resp.data.unratedCount || 0;
          } else {
            this.counts = {};
            this.totalCwr = 0;
            this.ratedCount = 0;
            this.unratedCount = 0;
          }
          this.rebuildChart();
        },
        error: () => {
          this.loading = false;
          this.loadError = true;
        },
      });
  }
}
